import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import Card from '../design-system/Card';
import { colors, spacing, radius, fontStack } from '../design-system/tokens';

/**
 * Copilot — the centerpiece of Curx AI.
 *
 * One question. One decision. No conversation history. No suggested
 * prompt chips. The state machine is intentionally tiny:
 *   idle → thinking → answered → idle
 *
 * The response is rendered through CruxInsight so it inherits the same
 * STATE / DO THIS / REASON / confidence visual language as the rest of
 * the app — meaning Copilot feels like the engine *behind* the system,
 * not a different product.
 *
 * Network: POST /api/copilot-decide  (Netlify function backed by Sonnet)
 *   → { state, actions[], reasons[], confidence }
 */
export default function Copilot() {
  const [phase, setPhase] = useState('idle'); // 'idle' | 'thinking' | 'answered' | 'error'
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null); // CruxInsight payload
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (phase === 'idle') {
      // Autofocus the input on first render and on reset.
      inputRef.current?.focus();
    }
  }, [phase]);

  const submit = async () => {
    const q = question.trim();
    if (!q || phase === 'thinking') return;

    setPhase('thinking');
    setErrorMessage('');
    setAnswer(null);

    // 30-second client-side timeout.
    const controller = new AbortController();
    abortRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), 30_000);

    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) {
        throw new Error('You need to be signed in to ask Curx.');
      }

      const res = await fetch('/api/copilot-decide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, question: q }),
        signal: controller.signal,
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || `Server returned ${res.status}`);
      }

      // New 5-section Decision shape:
      //   { insight, focus_now, start_here: { type, content }, next_steps[], outcome, confidence }
      // Validate every required field — server already enforces this, but
      // we guard the client too so a partial response can't render a broken
      // card.
      const sh = data?.start_here;
      const valid =
        data &&
        typeof data.insight === 'string' && data.insight.trim() &&
        typeof data.focus_now === 'string' && data.focus_now.trim() &&
        sh && typeof sh === 'object' &&
        (sh.type === 'action' || sh.type === 'search') &&
        typeof sh.content === 'string' && sh.content.trim() &&
        Array.isArray(data.next_steps) && data.next_steps.length > 0 &&
        typeof data.outcome === 'string' && data.outcome.trim();

      if (!valid) {
        throw new Error('Curx returned an unexpected response. Try again.');
      }

      // Normalise confidence to 0-1 (server returns 0-1; tolerate 0-100 too).
      let confidence = Number(data.confidence);
      if (!Number.isFinite(confidence)) confidence = 0.75;
      if (confidence > 1) confidence = confidence / 100;
      confidence = Math.max(0, Math.min(1, confidence));

      setAnswer({
        insight: data.insight.trim(),
        focus_now: data.focus_now.trim(),
        start_here: { type: sh.type, content: sh.content.trim() },
        next_steps: data.next_steps.map((s) => String(s).trim()).filter(Boolean).slice(0, 5),
        outcome: data.outcome.trim(),
        confidence,
      });
      setPhase('answered');
    } catch (e) {
      if (e?.name === 'AbortError') {
        setErrorMessage('The request took too long. Please try again.');
      } else {
        setErrorMessage(e?.message || 'Something went wrong. Try again.');
      }
      setPhase('error');
    } finally {
      clearTimeout(timeoutId);
      abortRef.current = null;
    }
  };

  const reset = () => {
    abortRef.current?.abort();
    setQuestion('');
    setAnswer(null);
    setErrorMessage('');
    setPhase('idle');
  };

  const onKeyDown = (e) => {
    // Cmd+Enter / Ctrl+Enter to submit.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.lg,
        minHeight: '70vh',
        fontFamily: fontStack,
      }}
    >
      {/* Minimal page header */}
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: colors.textSecondary,
          fontWeight: 600,
        }}
      >
        Copilot
      </div>

      {/* Main composer */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          minHeight: 360,
          padding: `${spacing.xl}px 0`,
        }}
      >
        {phase === 'answered' ? (
          <Answered answer={answer} onAskAnother={reset} />
        ) : phase === 'error' ? (
          <ErrorView message={errorMessage} onRetry={reset} />
        ) : (
          <Composer
            inputRef={inputRef}
            value={question}
            onChange={setQuestion}
            onKeyDown={onKeyDown}
            onSubmit={submit}
            phase={phase}
          />
        )}
      </div>
    </div>
  );
}

/* ================================================================
   Composer (idle + thinking)
   ================================================================ */

function Composer({ inputRef, value, onChange, onKeyDown, onSubmit, phase }) {
  const isThinking = phase === 'thinking';
  const len = value.length;
  const showCounter = len >= 800;
  const overLimit = len >= 1000;
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 640,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        alignItems: 'center',
      }}
    >
      <div style={{ width: '100%', position: 'relative' }}>
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, 1000))}
          onKeyDown={onKeyDown}
          disabled={isThinking}
          rows={3}
          maxLength={1000}
          placeholder="What should I do today?"
          style={{
            width: '100%',
            padding: '18px 20px',
            fontSize: 18,
            lineHeight: 1.5,
            fontFamily: fontStack,
            color: colors.textPrimary,
            backgroundColor: colors.card,
            border: `1px solid ${overLimit ? 'var(--danger)' : colors.border}`,
            borderRadius: radius.lg,
            outline: 'none',
            resize: 'vertical',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
            boxSizing: 'border-box',
          }}
        />
        {showCounter && (
          <span
            aria-live="polite"
            style={{
              position: 'absolute',
              right: 14,
              bottom: 10,
              fontSize: 11,
              color: overLimit ? 'var(--danger)' : colors.textSecondary,
              fontVariantNumeric: 'tabular-nums',
              fontFamily: fontStack,
              pointerEvents: 'none',
            }}
          >
            {len}/1000
          </span>
        )}
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: colors.textSecondary,
          textAlign: 'center',
          maxWidth: 480,
          lineHeight: 1.5,
        }}
      >
        Curx reads your energy, growth, and system signals to give you one decision.
      </p>

      {isThinking ? (
        <Thinking />
      ) : (
        <button
          type="button"
          onClick={onSubmit}
          disabled={!value.trim()}
          style={{
            padding: '12px 32px',
            borderRadius: 999,
            border: 'none',
            backgroundColor: value.trim() ? colors.primary : '#C7CEDB',
            color: '#fff',
            fontSize: 15,
            fontWeight: 600,
            fontFamily: fontStack,
            cursor: value.trim() ? 'pointer' : 'not-allowed',
            boxShadow: value.trim() ? '0 4px 12px rgba(47, 91, 255, 0.18)' : 'none',
            transition: 'transform 120ms ease, background-color 120ms ease',
          }}
        >
          Ask
        </button>
      )}
      <small
        style={{
          fontSize: 11,
          color: colors.textSecondary,
          letterSpacing: '0.04em',
        }}
      >
        ⌘ + Enter to submit
      </small>
    </div>
  );
}

/* ================================================================
   Thinking — calm pulsing dot, not a spinner.
   ================================================================ */

function Thinking() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        color: colors.textSecondary,
        fontFamily: fontStack,
        fontSize: 14,
      }}
    >
      <span
        aria-hidden="true"
        className="crux-pulse-dot"
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: colors.primary,
        }}
      />
      <span>Reading your signals…</span>
      <style>{`
        @keyframes crux-pulse {
          0%, 100% { opacity: 0.35; transform: scale(0.9); }
          50%      { opacity: 1;    transform: scale(1.1); }
        }
        .crux-pulse-dot {
          animation: crux-pulse 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

/* ================================================================
   Answered — single CruxInsight + "Ask another"
   ================================================================ */

function Answered({ answer, onAskAnother }) {
  return (
    <div
      className="crux-fade-in"
      style={{
        width: '100%',
        // Narrower than the previous 1200 wide layout: this is meant to feel
        // focused, like a single decision, not a dashboard.
        maxWidth: 800,
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        alignItems: 'center',
      }}
    >
      <DecisionCard answer={answer} />
      <button
        type="button"
        onClick={onAskAnother}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '8px 16px',
          color: colors.primary,
          fontFamily: fontStack,
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          textDecoration: 'underline',
          textUnderlineOffset: 4,
        }}
      >
        Ask another
      </button>
      <style>{`
        @keyframes crux-fade {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .crux-fade-in {
          animation: crux-fade 280ms ease-out both;
        }
      `}</style>
    </div>
  );
}

/* ================================================================
   DecisionCard — Copilot-only, NOT shared.
   5 stacked sections: INSIGHT / FOCUS NOW / START HERE / NEXT STEPS / OUTCOME.
   The shared CruxInsight component (used by Energy/Growth/System/Home)
   is intentionally not used here — Copilot's product surface is now
   "one decision", not a 3-column dashboard.
   ================================================================ */

const CIRCLED_NUMERALS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨'];

function DecisionCard({ answer }) {
  const confidencePct = Math.round((answer.confidence ?? 0.75) * 100);

  return (
    <Card accentColor={colors.primary} padding={0}>
      <div className="curx-decision">
        {/* Header */}
        <div className="curx-decision__head">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <SparkleGlyph color={colors.primary} />
            <span
              style={{
                fontSize: 12,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: colors.textSecondary,
                fontWeight: 600,
              }}
            >
              Curx Decision
            </span>
          </div>
          <span
            aria-label={`Confidence ${confidencePct}%`}
            style={{
              fontSize: 12,
              color: colors.textSecondary,
              fontWeight: 500,
              letterSpacing: '0.04em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {confidencePct}% confidence
          </span>
        </div>

        {/* INSIGHT */}
        <Section label="Insight">
          <p
            style={{
              margin: 0,
              fontSize: 16,
              lineHeight: 1.6,
              color: colors.textPrimary,
            }}
          >
            {answer.insight}
          </p>
        </Section>

        <Divider />

        {/* FOCUS NOW — visually dominant */}
        <Section label="Focus Now">
          <p
            className="curx-decision__focus"
            style={{
              margin: 0,
              fontWeight: 600,
              color: colors.textPrimary,
              letterSpacing: '-0.005em',
            }}
          >
            {answer.focus_now}
          </p>
        </Section>

        <Divider />

        {/* START HERE — action or search */}
        <Section label="Start Here">
          <StartHereBlock data={answer.start_here} />
        </Section>

        <Divider />

        {/* NEXT STEPS */}
        <Section label="Next Steps">
          <ol
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {answer.next_steps.map((step, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  fontSize: 15,
                  lineHeight: 1.55,
                  color: colors.textPrimary,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: 20,
                    lineHeight: 1,
                    color: colors.primary,
                    fontWeight: 600,
                    flexShrink: 0,
                    marginTop: -2,
                  }}
                >
                  {CIRCLED_NUMERALS[i] || `${i + 1}.`}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </Section>

        <Divider />

        {/* OUTCOME */}
        <Section label="Outcome">
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.55,
              color: colors.textSecondary,
              fontStyle: 'italic',
            }}
          >
            {answer.outcome}
          </p>
        </Section>
      </div>

      <style>{`
        .curx-decision {
          padding: 40px;
          display: flex;
          flex-direction: column;
          gap: 32px;
          font-family: ${fontStack};
        }
        .curx-decision__head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .curx-decision__focus {
          font-size: 22px;
          line-height: 1.4;
        }
        @media (max-width: 768px) {
          .curx-decision {
            padding: 24px;
            gap: 24px;
          }
          .curx-decision__focus {
            font-size: 19px;
          }
        }
      `}</style>
    </Card>
  );
}

function Section({ label, children }) {
  return (
    <section>
      <div
        style={{
          fontSize: 12,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.textSecondary,
          fontWeight: 600,
          marginBottom: 10,
          fontFamily: fontStack,
        }}
      >
        {label}
      </div>
      {children}
    </section>
  );
}

function Divider() {
  return (
    <div
      aria-hidden="true"
      style={{
        height: 1,
        backgroundColor: colors.border,
      }}
    />
  );
}

/* ================================================================
   StartHereBlock — renders either an action or a search prompt.
   ================================================================ */

function StartHereBlock({ data }) {
  if (!data) return null;
  if (data.type === 'search') return <StartHereSearch query={data.content} />;
  return <StartHereAction content={data.content} />;
}

function StartHereAction({ content }) {
  // If the content contains backtick-quoted commands, render those segments
  // monospace. Otherwise render as plain prose (e.g. "Open Notion, create…").
  const hasCommand = /`[^`]+`/.test(content);
  return (
    <div
      style={{
        background: 'var(--bg-surface-2)',
        border: `1px solid ${colors.border}`,
        borderRadius: radius.md,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.primary,
          fontWeight: 700,
        }}
      >
        <BoltGlyph color={colors.primary} /> Do this now
      </div>
      <div
        style={{
          fontSize: 15,
          lineHeight: 1.6,
          color: colors.textPrimary,
          fontFamily: hasCommand ? undefined : fontStack,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {hasCommand ? renderWithMonospace(content) : content}
      </div>
    </div>
  );
}

function renderWithMonospace(text) {
  // Split on backtick-quoted segments and render those in mono font.
  const parts = String(text).split(/(`[^`]+`)/);
  return parts.map((p, i) => {
    if (p.startsWith('`') && p.endsWith('`') && p.length > 2) {
      return (
        <code
          key={i}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: '0.92em',
            background: '#EEF2FF',
            color: '#1E3FAA',
            padding: '2px 6px',
            borderRadius: 6,
            wordBreak: 'break-word',
          }}
        >
          {p.slice(1, -1)}
        </code>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function StartHereSearch({ query }) {
  const [copied, setCopied] = useState(false);
  const trimmed = String(query || '').trim();
  const encoded = encodeURIComponent(trimmed);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(trimmed);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      // Fallback: select the visible query so the user can copy manually.
      setCopied(false);
    }
  };

  const links = [
    { label: 'YouTube',    url: `https://www.youtube.com/results?search_query=${encoded}` },
    { label: 'Google',     url: `https://www.google.com/search?q=${encoded}` },
    { label: 'Perplexity', url: `https://www.perplexity.ai/search?q=${encoded}` },
  ];

  return (
    <div
      style={{
        background: 'var(--bg-surface-2)',
        border: `1px solid ${colors.border}`,
        borderRadius: radius.md,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.primary,
          fontWeight: 700,
        }}
      >
        <SearchGlyph color={colors.primary} /> Search for this
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 15,
            color: colors.textPrimary,
            background: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            padding: '8px 12px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            wordBreak: 'break-word',
            flex: '1 1 240px',
          }}
        >
          “{trimmed}”
        </span>
        <button
          type="button"
          onClick={handleCopy}
          aria-live="polite"
          style={{
            padding: '8px 14px',
            minHeight: 36,
            borderRadius: 999,
            border: `1px solid ${colors.primary}`,
            background: copied ? colors.primary : 'transparent',
            color: copied ? '#fff' : colors.primary,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: fontStack,
            cursor: 'pointer',
            transition: 'background-color 120ms ease, color 120ms ease',
            flexShrink: 0,
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        {links.map((l) => (
          <a
            key={l.label}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '8px 14px',
              minHeight: 36,
              borderRadius: 999,
              background: '#fff',
              border: `1px solid ${colors.border}`,
              color: colors.textPrimary,
              fontSize: 13,
              fontWeight: 500,
              fontFamily: fontStack,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              transition: 'background-color 120ms ease, border-color 120ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
              e.currentTarget.style.borderColor = '#C7CEDB';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fff';
              e.currentTarget.style.borderColor = colors.border;
            }}
          >
            Search on {l.label} <span aria-hidden="true">↗</span>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ================================================================
   Glyphs — local to Copilot, small and lightweight.
   ================================================================ */

function SparkleGlyph({ color }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />
      <path d="M19 17l.8 2.2L22 20l-2.2.8L19 23l-.8-2.2L16 20l2.2-.8L19 17z" />
    </svg>
  );
}
function BoltGlyph({ color }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="13 2 4 14 11 14 10 22 20 10 13 10 13 2" />
    </svg>
  );
}
function SearchGlyph({ color }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

/* ================================================================
   ErrorView
   ================================================================ */

function ErrorView({ message, onRetry }) {
  return (
    <div
      style={{
        maxWidth: 480,
        padding: spacing.lg,
        borderRadius: radius.lg,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.card,
        textAlign: 'center',
        fontFamily: fontStack,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: colors.textPrimary,
          marginBottom: 6,
        }}
      >
        Something went wrong.
      </div>
      <div
        style={{
          fontSize: 13,
          color: colors.textSecondary,
          marginBottom: 14,
          lineHeight: 1.5,
        }}
      >
        {message || 'Please try again.'}
      </div>
      <button
        type="button"
        onClick={onRetry}
        style={{
          padding: '10px 20px',
          borderRadius: 999,
          border: 'none',
          backgroundColor: colors.primary,
          color: '#fff',
          fontSize: 14,
          fontWeight: 600,
          fontFamily: fontStack,
          cursor: 'pointer',
        }}
      >
        Try again
      </button>
    </div>
  );
}
